import type {
  InputFieldDefinition,
  OutputTypeEnum,
  PaletteStepWithSettings,
  StepWithSettings,
  TemplateListItem,
  TemplateWithSettings,
  UiPaletteStep,
  UiTemplateDetail,
  UiTemplateItem,
  UiTemplateSection,
  UiVariableGroup,
  Variable,
} from "./types";
import type { Json } from "@/types/supabase.types";
import {
  normalizeOutputTypes,
  parseOutputType,
  parseOutputTypes,
} from "./output-types";

export function extractContext(displayDescription: string | null | undefined) {
  return typeof displayDescription === "string" && displayDescription.trim()
    ? displayDescription
    : null;
}

export function fallbackOutputTypesFromSettings(
  settings: Json | null | undefined,
): OutputTypeEnum[] {
  if (!settings || typeof settings !== "object" || Array.isArray(settings)) {
    return ["markdown"];
  }

  const config = settings as Record<string, unknown>;

  const outputTypes = parseOutputTypes(config.outputTypes);
  if (outputTypes.length > 0) return outputTypes;

  const legacyOutputType = parseOutputType(config.outputFormat);
  if (legacyOutputType) return [legacyOutputType];

  return ["markdown"];
}

function resolveOutputTypes(
  types: OutputTypeEnum[] | null | undefined,
  settings: Json | null | undefined,
) {
  const normalizedTypes = normalizeOutputTypes(types ?? null);
  if (normalizedTypes.length > 0) return normalizedTypes;
  return fallbackOutputTypesFromSettings(settings);
}

export function toInputFieldDefinition(
  variable: Variable,
): InputFieldDefinition {
  const sourceId = variable.source_variable_id ?? null;
  return {
    key: variable.variable_name,
    type: variable.variable_type,
    description: variable.variable_description ?? undefined,
    source: sourceId ? "variable" : "custom",
    variableId: variable.variable_id,
    sourceVariableId: sourceId ?? undefined,
    variableNameFrontend: variable.variable_name_frontend,
    variableGroup: variable.variable_group,
    variableKind: variable.variable_kind,
    variableOptions: variable.variable_options,
    variableLinkedSchema: variable.variable_linked_schema,
    variableLinkedTable: variable.variable_linked_table,
    variableLinkedColumnId: variable.variable_linked_column_id,
    variableLinkedColumnName: variable.variable_linked_column_name,
    defaultValue: variable.variable_default,
    required: variable.variable_is_required,
    visible: variable.variable_is_visible,
    order: variable.variable_order,
    tier: variable.variable_tier,
    parentVariableId: variable.parent_variable_id,
  };
}

export function toInputResults(
  variables: Variable[],
  values: import("./types").VariableValue[],
): Record<string, Json> {
  const variablesById = new Map(variables.map((row) => [row.variable_id, row]));
  const result: Record<string, Json> = {};
  for (const value of values) {
    const variable = variablesById.get(value.variable_id);
    if (variable) result[variable.variable_name] = value.variable_value;
  }
  return result;
}

export function isInheritedGlobalStep(settings: Json | null | undefined) {
  if (!settings || typeof settings !== "object" || Array.isArray(settings)) {
    return false;
  }

  return (
    (settings as Record<string, unknown>)
      .inherited_from_global_template_step === true
  );
}

export function toUiItem(item: TemplateListItem): UiTemplateItem {
  const context = extractContext(
    item.template.template_setting?.display_description,
  );
  return {
    id: item.template.analysis_id ?? "",
    name:
      item.template.template_setting?.display_name ??
      item.template.analysis_name ??
      "",
    status: item.template.analysis_status ?? "template",
    createdAt: item.template.record_created ?? "",
    type: item.template.analysis_type ?? "",
    context,
    recordTenantId: item.template.record_tenant_id ?? "",
    isGlobal: item.isGlobal,
  };
}

export function toUiDetail(row: TemplateWithSettings): UiTemplateDetail {
  const context = extractContext(row.template_setting?.display_description);
  const valuesByVariableId = new Map(
    (row.variable_values ?? []).map((value) => [value.variable_id, value]),
  );
  const scopedInputFields = (row.variables ?? []).map((variable) => {
    const definition = toInputFieldDefinition(variable);
    const value = valuesByVariableId.get(variable.variable_id);
    return value
      ? { ...definition, configuredValue: value.variable_value, hasValue: true }
      : definition;
  });
  return {
    ...row,
    context,
    inputFields: scopedInputFields.filter(
      (field) => field.parentVariableId === null,
    ),
    scopedInputFields,
    inputResults: toInputResults(
      row.variables ?? [],
      row.variable_values ?? [],
    ),
  };
}

export function toUiSection(row: StepWithSettings): UiTemplateSection {
  const setting = row.templatestep_setting;
  const outputTypes = resolveOutputTypes(
    setting?.display_output_types,
    setting?.settings,
  );
  const valuesByVariableId = new Map(
    (row.variable_values ?? []).map((value) => [value.variable_id, value]),
  );
  const scopedInputFields = (row.variables ?? []).map((variable) => {
    const definition = toInputFieldDefinition(variable);
    const value = valuesByVariableId.get(variable.variable_id);
    return value
      ? { ...definition, configuredValue: value.variable_value, hasValue: true }
      : definition;
  });
  return {
    id: row.analysis_step_id,
    name: setting?.display_name ?? row.analysis_step_name,
    order: row.analysis_order,
    recordCreator: row.record_creator,
    recordUpdator: row.record_updator,
    recordTenantId: row.record_tenant_id,
    description: setting?.display_description ?? null,
    outputTypes,
    inputFields: scopedInputFields.filter(
      (field) => field.parentVariableId === null,
    ),
    scopedInputFields,
    inputResults: toInputResults(
      row.variables ?? [],
      row.variable_values ?? [],
    ),
    variables: row.variables ?? [],
    isReadOnly: isInheritedGlobalStep(setting?.settings),
  };
}

export function toUiPaletteStep(row: PaletteStepWithSettings): UiPaletteStep {
  const setting = row.templatestep_setting;
  const outputTypes = resolveOutputTypes(
    setting?.display_output_types,
    setting?.settings,
  );
  return {
    templateId: row.analysis_id,
    id: row.analysis_step_id,
    name: setting?.display_name ?? row.analysis_step_name,
    order: row.analysis_order,
    description: setting?.display_description ?? null,
    outputTypes,
  };
}

export function groupVariablesByGroup(
  variables: Variable[],
): UiVariableGroup[] {
  const groups = variables.reduce<Map<string, Variable[]>>((acc, variable) => {
    const group = variable.variable_group?.trim() || "Other";
    const existing = acc.get(group) ?? [];
    existing.push(variable);
    acc.set(group, existing);
    return acc;
  }, new Map());

  return [...groups.entries()].map(([group, groupVariables]) => ({
    group,
    variables: groupVariables,
  }));
}
